import { Injectable } from '@angular/core';
import Web3 from 'web3';
import { environment } from 'src/environments/environment';
import { readContract } from '@wagmi/core';
import { getAddress, encodeFunctionData } from 'viem';
import { publicClientConfig } from './publicClientConfig.service';
import { StorageService } from './storage.service';
import { wagmi_config } from '../core/config/wagmi_config';

const abi = require('src/app/core/common/abis/factory_abi.json');
const nftAbi = require('src/app/core/common/abis/nftAbi.json');


@Injectable({
    providedIn: 'root',
})

/**
 * Export Class
 */
export class ContractIntegrationsService {
    contract: any;
    private publicClient: any;
    account: any

    constructor(
        public publicClientConfig: publicClientConfig,
        private storageService: StorageService
    ) {
        this.onInit()

    }


    /**
     * init on
     */
    public onInit() {
        setTimeout(() => {
            this.accountConfig();
        }, 1000);
    }

    accountConfig() {
        this.account = this.storageService.getItem('wagmi_new_store') === null ?
            { address: '', network: '', chainId: '', provider: '' } :
            JSON.parse(this.storageService.getItem('wagmi_new_store') as any);
        this.publicClient = this.publicClientConfig.publicClient(this?.account?.state?.data?.chain?.id);
    }
    /**
     * Owners contract integrations service
     */
    public async owner() {
        await this.accountConfig();
        const chainId = this?.account?.state?.data?.chain?.id;
        return await readContract(wagmi_config, {
            address: environment[chainId].FACTORY_ADDRESS,
            abi: abi,
            chainId: Number(chainId),
            functionName: 'owner',
            args: []
        });

    }

    /**
     * Gets user created collections
     * @param {string} accountAddress
     * @returns
     */
    public async getCollections(regulated: boolean) {
        await this.accountConfig();
        const chainId = this?.account?.state?.data?.chain?.id;
        return await readContract(wagmi_config, {
            address: environment[chainId].FACTORY_ADDRESS,
            abi: abi,
            chainId: Number(chainId),
            functionName: regulated ? 'getAllCollectionsRegulated' : 'getAllCollectionsUnregulated',
            args: []
        });
    }

    /**
     * Retrieves fee details for regulated or unregulated transactions.
     * The function calls a smart contract to fetch platform, royalty, and client-related fees.
     *
     * @param {boolean} regulated 
     * @returns {Promise<IFeeDetails>} - A promise resolving to an object containing fee details
     */
    getFees(regulated: boolean): Promise<IFeeDetails> {
        return Promise.resolve(
            (async () => {
                let requestObj = {
                    address: environment[this?.account?.state?.data?.chain?.id].FACTORY_ADDRESS,
                    abi: abi,
                    functionName: regulated ? 'brokerSalesFees' : 'secondarySalesFees',
                    args: []
                }
                const feeDetails = await readContract(wagmi_config, requestObj);
                return {
                    platformFee: feeDetails[0],
                    royaltyFee: feeDetails[1],
                    creatorRoyaltyFee: feeDetails[2],
                    clientRoyaltyFee: feeDetails[3]
                };
            })()
        );
    }


    /**
     * Sets fees
     * @param {any} fees 
     * @param {string} walletAddress 
     * @param {boolean} regulated 
     * @returns  
     */
    async setFees(fees: any, walletAddress: string, regulated: boolean) {
        let { platformFee, royaltyFee, creatorRoyaltyFee, clientRoyaltyFee } = fees;
        let requiredGas;
        let feesAbi;
        const publicClient = this.publicClientConfig.publicClient(this?.account?.state?.data?.chain?.id);
        let args = regulated ? [platformFee, creatorRoyaltyFee, clientRoyaltyFee] : [platformFee, royaltyFee, creatorRoyaltyFee, clientRoyaltyFee];
        const contractParams = {
            address: environment[this?.account?.state?.data?.chain?.id].FACTORY_ADDRESS,
            abi: abi,
            functionName: regulated ? 'setBrokerSalesFee' : 'setSecondarySalesFee',
            args
        }
        requiredGas = await publicClient.estimateContractGas({
            address: environment[this?.account?.state?.data?.chain?.id].FACTORY_ADDRESS,
            account: walletAddress,
            ...contractParams
        });

        feesAbi = await encodeFunctionData({
            ...contractParams
        })
        requiredGas = requiredGas.toString();
        console.log(requiredGas, "req")
        return { feesAbi, requiredGas };
    }

    /**
     * Sets partner fees
     * @param fees 
     * @param walletAddress 
     * @param type 
     * @returns  
     */
    async setPartnerFees(fees: any, walletAddress: string, type: boolean) {
        let requiredGas;
        let feesAbi;
        const publicClient = this.publicClientConfig.publicClient(this?.account?.state?.data?.chain?.id);
        let { partner, platform_fee, royalty_fee, creator_royalty_fee, client_royalty_fee, use_partner_fee } = fees;
        const args = type ? [partner, platform_fee, creator_royalty_fee, client_royalty_fee, use_partner_fee] : [partner, platform_fee, royalty_fee, creator_royalty_fee, client_royalty_fee, use_partner_fee]
        const contractParams = {
            address: environment[this?.account?.state?.data?.chain?.id].FACTORY_ADDRESS,
            abi: abi,
            functionName: type ? 'setPartnerFeesBrokerSales' : 'setPartnerFeesSecondarySales',
            args
        }
        // console.log(contractParams, 'Contract Params')
        requiredGas = await publicClient.estimateContractGas({
            address: environment[this?.account?.state?.data?.chain?.id].FACTORY_ADDRESS,
            account: walletAddress,
            ...contractParams
        });

        feesAbi = await encodeFunctionData({
            ...contractParams
        })

        requiredGas = requiredGas.toString();
        return { feesAbi, requiredGas };
    }

    /**
     * Collections platform fee
     * @param {string} collectionAddress
     */
    public async collectionPlatformFee(collectionAddress) {
        const chainId = this?.account?.state?.data?.chain?.id;
        return await readContract(wagmi_config, {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            chainId: Number(chainId),
            functionName: 'platformFee',
            args: []
        });
    }

    /**
     * Collections royalty fee
     * @param {string} collectionAddress
     */
    public async collectionRoyaltyFee(collectionAddress) {
        const chainId = this?.account?.state?.data?.chain?.id;
        return await readContract(wagmi_config, {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            chainId: Number(chainId),
            functionName: 'royaltyFee',
            args: []
        });
    }


    /**
     * Sets collection platform fee
     * @param {any} amount
     * @param {string} collectionAddress
     */
    public async setCollectionPlatformFee(amount: any, collectionAddress: string, walletAddress: string) {
        let requiredGas;
        let platformFeeAbi;
        const publicClient = this.publicClientConfig.publicClient(this?.account?.state?.data?.chain?.id);
        const contractParams = {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            functionName: 'setPlatformFee',
            args: [amount],
        }

        requiredGas = await publicClient.estimateContractGas({
            address: await getAddress(collectionAddress),
            account: walletAddress,
            ...contractParams
        });

        platformFeeAbi = await encodeFunctionData({
            ...contractParams
        })

        requiredGas = requiredGas.toString();
        return { platformFeeAbi, requiredGas };
    }

    /**
     * Sets collection platform fee
     * @param {any} amount
     * @param {string} collectionAddress
     */
    public async setCollectionRoyaltyFee(amount: any, collectionAddress: string, walletAddress: string) {
        let requiredGas;
        let royaltyformFeeAbi;
        const publicClient = this.publicClientConfig.publicClient(this?.account?.state?.data?.chain?.id);
        const contractParams = {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            functionName: 'setRoyaltyFee',
            args: [amount],
        }

        requiredGas = await publicClient.estimateContractGas({
            address: await getAddress(collectionAddress),
            account: walletAddress,
            ...contractParams
        });

        royaltyformFeeAbi = await encodeFunctionData({
            ...contractParams
        })
        requiredGas = requiredGas.toString();
        return { royaltyformFeeAbi, requiredGas }
    }

    /**
     * Destroys collection
     * @param {string} collectionAddress
     * @returns
     */
    public async destroyCollection(collectionAddress, walletAddress) {
        let requiredGas;
        let destroyedAbi;
        const publicClient = this.publicClientConfig.publicClient(this?.account?.state?.data?.chain?.id);
        const contractParams = {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            functionName: 'destroyCollection',
            args: [],
        }

        requiredGas = await publicClient.estimateContractGas({
            address: await getAddress(collectionAddress),
            account: walletAddress,
            ...contractParams
        });

        destroyedAbi = await encodeFunctionData({
            ...contractParams
        })
        requiredGas = requiredGas.toString();
        return { destroyedAbi, requiredGas };
    }

    /**
     * Determines whether collection destroyed is
     * @param {string} collectionAddress
     * @returns
     */
    public async isCollectionDestroyed(collectionAddress) {
        const chainId = this?.account?.state?.data?.chain?.id;
        console.log(chainId, "chainId")
        return await readContract(wagmi_config, {
            address: environment[this?.account?.state?.data?.chain?.id].FACTORY_ADDRESS,
            abi: abi,
            chainId: Number(chainId),
            functionName: 'isCollectionDestroyed',
            args: [collectionAddress]
        });
    }

    /**
     * Gets user created collections
     * @param {string} accountAddress
     * @returns
     */
    public async getCollectionName(collectionAddress) {
        const chainId = this?.account?.state?.data?.chain?.id;
        return await readContract(wagmi_config, {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            chainId: Number(chainId),
            functionName: 'name',
            args: []
        });
    }

    public async getCollectionOwner(collectionAddress) {
        const chainId = this?.account?.state?.data?.chain?.id;
        return await readContract(wagmi_config, {
            address: await getAddress(collectionAddress),
            abi: nftAbi,
            chainId: Number(chainId),
            functionName: 'owner',
            args: []
        });
    }
}

export interface IFeeDetails {
    platformFee: number;
    royaltyFee: number;
    creatorRoyaltyFee: number;
    clientRoyaltyFee: number;
}
